import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, CalendarIcon } from "lucide-react";
import { format, parse, isValid } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import FundPerformanceEditor from "@/components/FundPerformanceEditor";
import MeetingLogEditor from "@/components/MeetingLogEditor";

import CommentaryEditorSection from "@/components/CommentaryEditorSection";

const formatDateForDisplay = (date: Date | undefined): string => {
  if (!date) return "";
  return format(date, "dd/MM/yyyy");
};

const parseDateFromInput = (value: string): Date | undefined => {
  if (!value) return undefined;
  const parsed = parse(value, "dd/MM/yyyy", new Date());
  return isValid(parsed) ? parsed : undefined;
};

const AdviserAssistEditor = () => {
  const { toast } = useToast();
  const [lastMonthCommentary, setLastMonthCommentary] = useState("");
  const [latestCommentary, setLatestCommentary] = useState("");
  const [performanceMonitorUrl, setPerformanceMonitorUrl] = useState("");
  const [lastMonthCommentaryDate, setLastMonthCommentaryDate] = useState<Date | undefined>();
  const [latestCommentaryDate, setLatestCommentaryDate] = useState<Date | undefined>();
  const [lastMonthDateInput, setLastMonthDateInput] = useState("");
  const [latestDateInput, setLatestDateInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const { data, error } = await supabase
        .from('adviser_assist_content')
        .select('*')
        .limit(1)
        .single();

      if (error) {
        console.error('Error loading content:', error);
        return;
      }

      if (data) {
        setLastMonthCommentary(data.last_month_commentary || "");
        setLatestCommentary(data.latest_commentary || "");
        setPerformanceMonitorUrl(data.performance_monitor_url || "");
        
        if (data.last_month_commentary_date) {
          const date = new Date(data.last_month_commentary_date);
          setLastMonthCommentaryDate(date);
          setLastMonthDateInput(formatDateForDisplay(date));
        }
        if (data.latest_commentary_date) {
          const date = new Date(data.latest_commentary_date);
          setLatestCommentaryDate(date);
          setLatestDateInput(formatDateForDisplay(date));
        }
      }
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleLastMonthDateInputChange = (value: string) => {
    setLastMonthDateInput(value);
    const parsed = parseDateFromInput(value);
    if (parsed) {
      setLastMonthCommentaryDate(parsed);
    }
  };

  const handleLatestDateInputChange = (value: string) => {
    setLatestDateInput(value);
    const parsed = parseDateFromInput(value);
    if (parsed) {
      setLatestCommentaryDate(parsed);
    }
  };

  const handleUpdate = async () => {
    setIsLoading(true);
    const updatedAt = new Date().toISOString();
    
    try {
      const { data: userData } = await supabase.auth.getUser();
      
      // Update Supabase database
      const { error } = await supabase
        .from('adviser_assist_content')
        .update({
          last_month_commentary: lastMonthCommentary,
          latest_commentary: latestCommentary,
          performance_monitor_url: performanceMonitorUrl,
          last_month_commentary_date: lastMonthCommentaryDate ? format(lastMonthCommentaryDate, 'yyyy-MM-dd') : null,
          latest_commentary_date: latestCommentaryDate ? format(latestCommentaryDate, 'yyyy-MM-dd') : null,
          updated_at: updatedAt,
          updated_by: userData.user?.id
        })
        .neq('id', '00000000-0000-0000-0000-000000000000');
      
      if (error) throw error;

      // Sync to Google Sheet
      const { error: sheetError } = await supabase.functions.invoke('adviser-assist-sync', {
        body: {
          last_month_commentary: lastMonthCommentary,
          latest_commentary: latestCommentary,
          performance_monitor_url: performanceMonitorUrl,
          last_month_commentary_date: lastMonthCommentaryDate ? formatDateForDisplay(lastMonthCommentaryDate) : '',
          latest_commentary_date: latestCommentaryDate ? formatDateForDisplay(latestCommentaryDate) : '',
          updated_at: format(new Date(updatedAt), 'dd/MM/yyyy HH:mm:ss')
        }
      });

      if (sheetError) {
        console.error('Google Sheet sync error:', sheetError);
        toast({
          title: "Partial Success",
          description: "Database updated but Google Sheet sync failed.",
          variant: "destructive",
        });
        return;
      }
      
      toast({
        title: "Success",
        description: "Content updated in database and Google Sheet",
      });
    } catch (error) {
      console.error('Error updating content:', error);
      toast({
        title: "Error",
        description: "Failed to update content. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-8">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Twice-Monthly Adviser Assist Editor</h1>
        <p className="text-muted-foreground mt-2">
          Manage content for the twice-monthly adviser assistance communications
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Content Editor</CardTitle>
          <CardDescription>
            Update the commentary and performance monitor information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="last-month-commentary">Last Month Director's Commentary</Label>
            <Textarea
              id="last-month-commentary"
              placeholder="Enter last month's director's commentary..."
              value={lastMonthCommentary}
              onChange={(e) => setLastMonthCommentary(e.target.value)}
              rows={8}
              className="resize-y"
            />
          </div>

          <div className="space-y-2">
            <Label>Date of Last Month Director's Commentary</Label>
            <div className="flex gap-2">
              <Input
                placeholder="dd/mm/yyyy"
                value={lastMonthDateInput}
                onChange={(e) => handleLastMonthDateInputChange(e.target.value)}
                className="w-40"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-10 p-0",
                      !lastMonthCommentaryDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={lastMonthCommentaryDate}
                    onSelect={(date) => {
                      setLastMonthCommentaryDate(date);
                      setLastMonthDateInput(formatDateForDisplay(date));
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="latest-commentary">Latest Commentary</Label>
            <Textarea
              id="latest-commentary"
              placeholder="Enter the latest commentary..."
              value={latestCommentary}
              onChange={(e) => setLatestCommentary(e.target.value)}
              rows={8}
              className="resize-y"
            />
          </div>

          <div className="space-y-2">
            <Label>Date of Latest Commentary</Label>
            <div className="flex gap-2">
              <Input
                placeholder="dd/mm/yyyy"
                value={latestDateInput}
                onChange={(e) => handleLatestDateInputChange(e.target.value)}
                className="w-40"
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-10 p-0",
                      !latestCommentaryDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={latestCommentaryDate}
                    onSelect={(date) => {
                      setLatestCommentaryDate(date);
                      setLatestDateInput(formatDateForDisplay(date));
                    }}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="performance-monitor-url">Last Performance Monitor Email URL</Label>
            <Input
              id="performance-monitor-url"
              type="url"
              placeholder="https://example.com/performance-monitor"
              value={performanceMonitorUrl}
              onChange={(e) => setPerformanceMonitorUrl(e.target.value)}
            />
          </div>

          <Button
            onClick={handleUpdate}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {isLoading ? "Updating..." : "Update Content"}
          </Button>
        </CardContent>
      </Card>

      <FundPerformanceEditor />
      
      <MeetingLogEditor />
      <CommentaryEditorSection />
    </div>
  );
};

export default AdviserAssistEditor;
